import { Box, Show, SimpleGrid, Skeleton, Stack, Text } from '@chakra-ui/react';
import {
	DragDropContext,
	Draggable,
	Droppable,
	type DropResult,
} from '@hello-pangea/dnd';
import { type FC, useState } from 'react';
import FaceLiftSearchInput from '@/components/Atoms/FaceLiftSearchInput';
import FaceliftButton from '@/components/Atoms/FaceliftButton';

export type Item = { id: string; content: string; secondaryContent?: string };
export type Column = { id: string; title: string; itemIds: string[] };
export type Board = {
	item: Record<string, Item>;
	columns: Record<string, Column>;
	columnOrder: string[];
};

export interface FaceliftDraggableProps {
	data: Board;
	onChange: (newData: Board) => void;
	onSelectAll?: () => void;
	onClearAll?: () => void;
	selectAllText?: string;
	removeAllText?: string;
	hideActions?: boolean;
	searchValues?: Record<string, string>;
	onSearchChange?: (columnId: string, value: string) => void;
	isLoading?: boolean;
	height?: string;
}

const FaceliftDraggable: FC<FaceliftDraggableProps> = ({
	data,
	onChange,
	onSelectAll,
	onClearAll,
	selectAllText = 'Select All',
	removeAllText = 'Remove All',
	hideActions = false,
	searchValues,
	onSearchChange,
	isLoading = false,
	height = '200px',
}) => {
	const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
	const [internalSearchValues, setInternalSearchValues] = useState<Record<string, string>>({});

	const getFilteredItemIds = (columnId: string): string[] => {
		const column = data.columns[columnId];
		const searchValue = searchValues?.[columnId] ?? internalSearchValues[columnId] ?? '';

		if (!searchValue) {
			return column.itemIds;
		}

		return column.itemIds.filter((itemId) => {
			const item = data.item[itemId];
			return item.content.toLowerCase().includes(searchValue.toLowerCase());
		});
	};

	const onDragEnd = (result: DropResult) => {
		const { source, destination } = result;
		if (!destination) return;
		if (
			destination.droppableId === source.droppableId &&
			destination.index === source.index
		)
			return;

		const startCol = data.columns[source.droppableId];
		const endCol = data.columns[destination.droppableId];

		const sourceFilteredIds = getFilteredItemIds(source.droppableId);
		const destFilteredIds = getFilteredItemIds(destination.droppableId);

		const draggedItemId = sourceFilteredIds[source.index];

		if (startCol === endCol) {
			const newItemIds = Array.from(startCol.itemIds);
			const currentIndex = newItemIds.indexOf(draggedItemId);
			newItemIds.splice(currentIndex, 1);

			let insertIndex = 0;

			// Determine if we're dragging down or up
			const isDraggingDown = destination.index > source.index;

			if (destination.index === 0) {
				// Dragging to the very top
				insertIndex = newItemIds.indexOf(destFilteredIds[0]);
				if (insertIndex === -1) insertIndex = 0;
			} else if (destination.index >= destFilteredIds.length) {
				// Dragging beyond the last position - place at the very end
				const lastFilteredId = destFilteredIds[destFilteredIds.length - 1];
				insertIndex = newItemIds.indexOf(lastFilteredId) + 1;
			} else {
				// Dragging to a middle position
				const targetId = destFilteredIds[destination.index];
				const targetIndex = newItemIds.indexOf(targetId);

				if (isDraggingDown) {
					// When dragging down, insert AFTER the target position
					insertIndex = targetIndex + 1;
				} else {
					// When dragging up, insert BEFORE the target position
					insertIndex = targetIndex;
				}
			}

			newItemIds.splice(insertIndex, 0, draggedItemId);

			onChange({
				...data,
				columns: {
					...data.columns,
					[startCol.id]: { ...startCol, itemIds: newItemIds },
				},
			});
			return;
		}

		const startItemIds = Array.from(startCol.itemIds);
		const currentIndex = startItemIds.indexOf(draggedItemId);
		startItemIds.splice(currentIndex, 1);

		const endItemIds = Array.from(endCol.itemIds);

		let insertIndex = 0;
		if (destination.index === 0) {
			// Dragging to the top of the destination column
			insertIndex = destFilteredIds.length > 0 ? endItemIds.indexOf(destFilteredIds[0]) : 0;
			if (insertIndex === -1) insertIndex = 0;
		} else if (destination.index >= destFilteredIds.length) {
			// Dragging beyond the last position - place at the very end
			if (destFilteredIds.length > 0) {
				const lastFilteredId = destFilteredIds[destFilteredIds.length - 1];
				insertIndex = endItemIds.indexOf(lastFilteredId) + 1;
			} else {
				insertIndex = endItemIds.length;
			}
		} else {
			// Dragging to a middle position
			const beforeId = destFilteredIds[destination.index];
			insertIndex = endItemIds.indexOf(beforeId);
		}

		endItemIds.splice(insertIndex, 0, draggedItemId);

		onChange({
			...data,
			columns: {
				...data.columns,
				[startCol.id]: { ...startCol, itemIds: startItemIds },
				[endCol.id]: { ...endCol, itemIds: endItemIds },
			},
		});
	};

	const handleSelectAll = () => {
		if (onSelectAll) {
			onSelectAll();
			return;
		}

		const available = data.columns['column-1'];
		const current = data.columns['column-2'];

		onChange({
			...data,
			columns: {
				...data.columns,
				'column-1': { ...available, itemIds: [] },
				'column-2': {
					...current,
					itemIds: [...current.itemIds, ...available.itemIds],
				},
			},
		});
	};

	const handleClearAll = () => {
		if (onClearAll) {
			onClearAll();
			return;
		}

		const available = data.columns['column-1'];
		const current = data.columns['column-2'];

		onChange({
			...data,
			columns: {
				...data.columns,
				'column-1': {
					...available,
					itemIds: [...available.itemIds, ...current.itemIds],
				},
				'column-2': { ...current, itemIds: [] },
			},
		});
	};

	return (
		<DragDropContext onDragEnd={onDragEnd}>
			<SimpleGrid columns={{ base: 1, md: 2 }} gap={6} w="full">
				{data.columnOrder.map((colId: string) => {
					const column = data.columns[colId];

					const searchValue = searchValues?.[colId] ?? internalSearchValues[colId] ?? '';

					const items = column.itemIds
						.map((tId: string) => data.item[tId])
						.filter((task) =>
							task.content.toLowerCase().includes(searchValue.toLowerCase()),
						);

					return (
						<Box key={column.id} w="full">
							<Text
								fontStyle="italic"
								fontWeight="bold"
								fontSize="sm"
								color={{ _dark: 'brand.white', _light: 'gray.800' }}
								mb={3}
							>
								{column.title}
							</Text>

							<Box border="1px solid" rounded="md" px={1} pb={2}>
								<Box
									display="flex"
									justifyContent="flex-end"
									position="sticky"
									top="0"
									bg="brand.cardBackground"
									p={1}
								>
									<FaceLiftSearchInput
										value={searchValue}
										onChange={(e) => {
											if (onSearchChange) {
												onSearchChange(column.id, e.target.value);
											} else {
												setInternalSearchValues(prev => ({
													...prev,
													[column.id]: e.target.value
												}));
											}
										}}
										placeholder={`Search`}
										onClear={() => {
											if (onSearchChange) {
												onSearchChange(column.id, '');
											} else {
												setInternalSearchValues(prev => ({
													...prev,
													[column.id]: ''
												}));
											}
										}}
									/>
								</Box>

								<Droppable droppableId={column.id}>
									{(provided) => (
										<Box
											ref={provided.innerRef}
											{...provided.droppableProps}
											p={1}
											overflowY="auto"
											h={height}
										>
											<Show
												when={isLoading && column.itemIds.length > 0}
												fallback={
													<Box>
														{items.map((item: Item, index: number) => (
															<Draggable
																key={item.id}
																draggableId={item.id}
																index={index}
															>
																{(provided) => (
																	<Box
																		ref={provided.innerRef}
																		{...provided.draggableProps}
																		{...provided.dragHandleProps}
																		mb={1}
																		px={3}
																		py={1}
																		rounded="md"
																		bg="brand.draggableItemBackground"
																		color={{
																			_dark: 'brand.white',
																			_light: 'gray.800',
																		}}
																		fontSize="sm"
																		onMouseEnter={() => {
																			if (item.secondaryContent) {
																				setHoveredItemId(item.id);
																			}
																		}}
																		onMouseLeave={() => {
																			if (item.secondaryContent) {
																				setHoveredItemId(null);
																			}
																		}}
																	>
																		{item.secondaryContent ? hoveredItemId === item.id ? item.secondaryContent : item.content : item.content}
																	</Box>
																)}
															</Draggable>
														))}
													</Box>
												}
											>
												<Stack gap={2}>
													<Skeleton height="32px" rounded="md" />
													<Skeleton height="32px" rounded="md" />
													<Skeleton height="32px" rounded="md" />
													<Skeleton height="32px" rounded="md" />
												</Stack>
											</Show>
											{provided.placeholder}
										</Box>
									)}
								</Droppable>
							</Box>

							{!hideActions && (
								<Box display="flex" justifyContent="flex-end">
									{column.id === 'column-1' && (
										<FaceliftButton mt={4} width="32" onClick={handleSelectAll}>
											{selectAllText}
										</FaceliftButton>
									)}
									{column.id === 'column-2' && (
										<FaceliftButton mt={4} width="32" onClick={handleClearAll}>
											{removeAllText}
										</FaceliftButton>
									)}
								</Box>
							)}
						</Box>
					);
				})}
			</SimpleGrid>
		</DragDropContext>
	);
};

export default FaceliftDraggable;
